import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckCircle2, Loader2, MessageSquare, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getLanguage, trDisplay } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { sendFeedback } from '../lib/feedbackClient';

const categories = [
  ['bug', 'Bug'], ['general', 'Feedback generico'], ['improvement', 'Richiesta di miglioramento'],
];

export default function FeedbackButton() {
  useTranslation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState('idle');
  const sending = useRef(false);
  const submission = useRef(null);
  const fieldClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
  const changeOpen = next => {
    setOpen(next);
  };
  const submit = async event => {
    event.preventDefault();
    if (sending.current) return;
    if (details.trim().length < 10) { setStatus('invalid'); return; }
    sending.current = true;
    setStatus('sending');
    const content = JSON.stringify([category, details.trim(), email.trim()]);
    try {
      if (submission.current?.content !== content) {
        submission.current = { content, id: window.crypto.randomUUID() };
      }
      await sendFeedback({
        category, details: details.trim(), email: email.trim(), website,
        language: getLanguage(),
        submissionId: submission.current.id,
      });
      setStatus('success');
    } catch (error) {
      setStatus(error.code === 'rate-limit' ? 'rate-limit' : error.code === 'unavailable' ? 'unavailable' : 'error');
    } finally { sending.current = false; }
  };
  const errorText = {
    invalid: 'Scrivi almeno 10 caratteri per descrivere la segnalazione.',
    'rate-limit': 'Hai inviato diverse segnalazioni. Attendi qualche minuto e riprova: il testo è conservato.',
    unavailable: 'Invio temporaneamente non disponibile. Il testo è conservato: riprova più tardi.',
    error: 'Non è stato possibile confermare l’invio. Controlla la connessione e riprova: il testo è conservato.',
  }[status];
  return <Dialog.Root open={open} onOpenChange={changeOpen}>
    <Dialog.Trigger asChild>
      <Button type="button" variant="ghost" title={trDisplay('Invia feedback')} className="h-11 w-11 p-0 md:w-auto md:px-2 text-xs text-muted-foreground active:bg-muted focus-visible:ring-2 focus-visible:ring-offset-2" data-testid="feedback-open">
        <MessageSquare aria-hidden="true" /><span className="sr-only md:not-sr-only">{trDisplay('Invia feedback')}</span>
      </Button>
    </Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[90] bg-foreground/40" />
      <Dialog.Content className="fixed left-1/2 top-1/2 z-[100] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-4 text-foreground sm:p-6" onOpenAutoFocus={event => { event.preventDefault(); document.getElementById('feedback-details')?.focus(); }}>
        <div className="flex items-start justify-between gap-2">
          <Dialog.Title className="text-lg font-semibold">{trDisplay('Invia feedback')}</Dialog.Title>
          <Dialog.Close asChild><Button type="button" variant="ghost" className="-mr-2 -mt-2 h-11 w-11 shrink-0 p-0 focus-visible:ring-2 focus-visible:ring-offset-2" aria-label={trDisplay('Chiudi feedback')}><X aria-hidden="true" /></Button></Dialog.Close>
        </div>
        <Dialog.Description className="mb-5 mt-1 text-sm text-muted-foreground">{trDisplay('Segnala un problema o suggerisci come migliorare l’app.')}</Dialog.Description>
        {status === 'success' ? <div role="status" className="space-y-4 py-4">
          <CheckCircle2 aria-hidden="true" className="h-6 w-6" />
          <p className="font-medium">{trDisplay('Grazie! Il feedback è stato inviato.')}</p>
          <p className="text-sm text-muted-foreground">{trDisplay('Se hai lasciato un’email, potremo usarla per risponderti.')}</p>
          <div className="flex flex-wrap gap-2"><Dialog.Close asChild><Button type="button" variant="outline" className="h-11">{trDisplay('Chiudi')}</Button></Dialog.Close>
            <Button type="button" variant="ghost" className="h-11" onClick={() => { setStatus('idle'); setDetails(''); setEmail(''); submission.current = null; }}>{trDisplay('Scrivi un altro feedback')}</Button>
          </div>
        </div> : <form onSubmit={submit} className="space-y-4" aria-busy={status === 'sending'} data-testid="feedback-form">
          <fieldset disabled={status === 'sending'} className="space-y-4">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{trDisplay('Tipo di segnalazione')}</legend>
              <div className="flex flex-wrap gap-2">{categories.map(([value, label]) => <label key={value} className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted ${category === value ? 'border-foreground bg-muted' : 'border-border'}`}>
                <input type="radio" name="feedback-category" value={value} checked={category === value} onChange={() => setCategory(value)} className="focus-visible:ring-2 focus-visible:ring-ring" />{trDisplay(label)}
              </label>)}</div>
            </fieldset>
            <div>
              <label htmlFor="feedback-details" className="mb-2 block text-sm font-medium">{trDisplay('Dettagli')}</label>
              <textarea id="feedback-details" name="details" required minLength={10} maxLength={5000} rows={5} value={details} onChange={event => setDetails(event.target.value)} className={fieldClass} aria-describedby="feedback-privacy feedback-length" aria-invalid={status === 'invalid' || undefined} autoComplete="off" placeholder={trDisplay('Cosa è successo? Cosa ti aspettavi o cosa vorresti migliorare?')} />
              <p id="feedback-length" className="mt-1 text-xs text-muted-foreground">{details.length} / 5000</p>
            </div>
            <div>
              <label htmlFor="feedback-email" className="mb-2 block text-sm font-medium">{trDisplay('La tua email (facoltativa)')}</label>
              <Input id="feedback-email" name="email" type="email" inputMode="email" autoComplete="email" maxLength={254} value={email} onChange={event => setEmail(event.target.value)} className="h-11 focus-visible:ring-2" />
            </div>
            <div hidden aria-hidden="true"><label htmlFor="feedback-website">{trDisplay('Lascia vuoto questo campo')}</label><input id="feedback-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} /></div>
          </fieldset>
          <p id="feedback-privacy" className="text-xs text-muted-foreground">{trDisplay('Il messaggio viene inviato via email al gestore. Non includere dati sensibili. Parametri e risultati delle simulazioni non vengono allegati.')}</p>
          {errorText && <p role="alert" className="text-sm text-destructive">{trDisplay(errorText)}</p>}
          <div className="flex justify-end gap-2">
            <Dialog.Close asChild><Button type="button" variant="outline" className="h-11">{trDisplay(status === 'sending' ? 'Nascondi' : 'Annulla')}</Button></Dialog.Close>
            <Button type="submit" disabled={status === 'sending'} className="h-11 min-w-32 bg-[hsl(var(--swr-progress-accent))] text-primary-foreground hover:opacity-90 active:opacity-80 focus-visible:ring-2 focus-visible:ring-offset-2">
              {status === 'sending' && <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" />}{trDisplay(status === 'sending' ? 'Invio in corso…' : 'Invia feedback')}
            </Button>
          </div>
          <p className="sr-only" role="status">{status === 'sending' ? trDisplay('Invio in corso…') : ''}</p>
        </form>}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
